import { Injectable } from '@angular/core';
import { Consultation, Patient } from '@app/core/models';
import { ConsultationApiData } from '@app/core/models/consultation-api-data';
import { NutritionService, VetService } from '@app/core/services/network';
import { Helper, LifestageType, RxjsHelper, SpeciesCode } from '@app/shared/utils';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ObservablesLayerService {
  constructor(
    private nutritionService: NutritionService,
    private vetService: VetService,
  ) {}

  lifestage$(
    speciesCode$: Observable<SpeciesCode>,
    breedCode$: Observable<string>,
    dateOfBirth$: Observable<Date>,
  ): {
    lifestage$: Observable<LifestageType>;
    lifestageUpdating$: BehaviorSubject<boolean>;
    lifestageErrors$: Subject<string>;
  } {
    const lifestageUpdating$ = new BehaviorSubject<boolean>(false);

    const lifestageErrors$ = new Subject<string>();

    const lifestage$ = combineLatest([speciesCode$, breedCode$, dateOfBirth$]).pipe(
      RxjsHelper.filterCollFull(),
      RxjsHelper.distinctUntilCollElemChanged(),
      tap(() => {
        lifestageUpdating$.next(true);
      }),
      switchMap(([_speciesCode, breedCode, dateOfBirth]) =>
        this.nutritionService.getBreedPetProfile({ breedCode: breedCode as string, dateOfBirth }).pipe(
          map(({ lifestage }) => lifestage),
          catchError((err) => {
            lifestageErrors$.next(err);

            return of(null);
          }),
        ),
      ),
      tap(() => {
        lifestageUpdating$.next(false);
      }),
      shareReplay(1),
    );

    return { lifestageUpdating$, lifestage$, lifestageErrors$ };
  }

  isOld$(lifestage$: Observable<LifestageType>): Observable<boolean> {
    return lifestage$.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      map((lifestage: LifestageType) => Helper.lifestageOld(lifestage)),
      shareReplay(1),
    );
  }

  fetchPatientAndConsultations$(patientId: string): {
    patient$: Observable<Patient>;
    patientLoading$: BehaviorSubject<boolean>;
    patientError$: Subject<string>;
    lastConsultation$: Observable<Consultation>;
    lastConsultationLoading$: BehaviorSubject<boolean>;
    lastConsultationError$: Subject<string>;
    nextConsultation$: Observable<Consultation>;
    nextConsultationLoading$: BehaviorSubject<boolean>;
    nextConsultationError$: Subject<string>;
    consultations$: Observable<ConsultationApiData>;
    consultationsLoading$: BehaviorSubject<boolean>;
    consultationsError$: Subject<string>;
  } {
    const { patient$, patientLoading$, patientError$ } = this._fetchPatient$(patientId);
    const { consultations$, consultationsError$, consultationsLoading$ } = this._fetchConsultations$(patientId);
    const { lastConsultation$, lastConsultationError$, lastConsultationLoading$ } = this._fetchLastConsultations$(consultations$);
    const { nextConsultation$, nextConsultationError$, nextConsultationLoading$ } = this._fetchNextConsultation$(lastConsultation$);

    return {
      patient$,
      patientError$,
      patientLoading$,
      consultations$,
      consultationsError$,
      consultationsLoading$,
      lastConsultation$,
      lastConsultationError$,
      lastConsultationLoading$,
      nextConsultation$,
      nextConsultationError$,
      nextConsultationLoading$,
    };
  }

  // If patient is null, fetch it from vet service
  private _fetchPatient$(patientId: string): {
    patient$: Observable<Patient>;
    patientLoading$: BehaviorSubject<boolean>;
    patientError$: Subject<string>;
  } {
    const patientError$ = new Subject<string>();
    const patientLoading$ = new BehaviorSubject(false);
    patientLoading$.next(true);
    const patient$ = this.vetService.fetchPatient(patientId).pipe(
      catchError((error) => {
        patientError$.next(error);
        return of(null);
      }),
      tap(() => {
        patientLoading$.next(false);
      }),
      shareReplay(1),
    );
    return {
      patient$,
      patientLoading$,
      patientError$,
    };
  }

  // Fetch consultations
  private _fetchConsultations$(patientId: string): {
    consultations$: Observable<ConsultationApiData>;
    consultationsLoading$: BehaviorSubject<boolean>;
    consultationsError$: Subject<string>;
  } {
    const consultationsError$ = new Subject<string>();
    const consultationsLoading$ = new BehaviorSubject(true);

    const consultations$ = this.vetService.consultationsByPatient(patientId).pipe(
      catchError((error) => {
        consultationsError$.next(error);
        return of(null);
      }),
      tap(() => {
        consultationsLoading$.next(false);
      }),
      shareReplay(1),
    );

    return {
      consultations$,
      consultationsError$,
      consultationsLoading$,
    };
  }

  // Fetch consultations
  private _fetchLastConsultations$(consultations$: Observable<ConsultationApiData>): {
    lastConsultation$: Observable<Consultation>;
    lastConsultationLoading$: BehaviorSubject<boolean>;
    lastConsultationError$: Subject<string>;
    lastConsultationEnd$: Subject<boolean>;
  } {
    const lastConsultationError$ = new Subject<string>();
    const lastConsultationLoading$ = new BehaviorSubject(false);
    const lastConsultationEnd$ = new Subject<boolean>();

    lastConsultationLoading$.next(true);
    const lastConsultation$ = consultations$.pipe(
      map((data) => {
        if (data?.result?.length) {
          return data.result[0];
        }
        lastConsultationError$.next('_fetchLastConsultations$: Impossible to get lastConsultation from consultations$');
        return null;
      }),
      catchError((error) => {
        lastConsultationError$.next(error);
        return of(null);
      }),
      tap(() => {
        lastConsultationLoading$.next(false);
        lastConsultationEnd$.next(true);
        lastConsultationEnd$.complete();
      }),
      shareReplay(1),
    );

    return {
      lastConsultation$,
      lastConsultationError$,
      lastConsultationLoading$,
      lastConsultationEnd$,
    };
  }

  // Get called with the lastConsultation, which may or may not be complete with "next consultation" information.
  // If not, fetch them
  private _fetchNextConsultation$(lastConsultation$: Observable<Consultation>): {
    nextConsultation$: Observable<Consultation>;
    nextConsultationLoading$: BehaviorSubject<boolean>;
    nextConsultationError$: Subject<string>;
    nextConsultationEnd$: Subject<boolean>;
  } {
    const nextConsultationError$ = new Subject<string>();
    const nextConsultationLoading$ = new BehaviorSubject(false);
    const nextConsultationEnd$ = new Subject<boolean>();

    const nextConsultation$ = lastConsultation$.pipe(
      mergeMap((lastConsultation) => {
        if (!lastConsultation) {
          nextConsultationError$.next('_fetchNextConsultation$: lastConsultation is not supposed to be null.');
          nextConsultationEnd$.next(true);
          nextConsultationEnd$.complete();
          return of(null);
        }
        if (lastConsultation.nextVisit) {
          nextConsultationEnd$.next(true);
          nextConsultationEnd$.complete();
          return of(lastConsultation);
        } else {
          nextConsultationLoading$.next(true);
          return this.vetService.fetchConsultationById(lastConsultation.patientId, lastConsultation.id).pipe(
            catchError((error) => {
              nextConsultationError$.next(error);
              return of(null);
            }),
            tap(() => {
              nextConsultationLoading$.next(false);
              nextConsultationEnd$.next(true);
              nextConsultationEnd$.complete();
            }),
          );
        }
      }),
      shareReplay(1),
    );

    return {
      nextConsultation$,
      nextConsultationError$,
      nextConsultationLoading$,
      nextConsultationEnd$,
    };
  }
}
